import { useEffect, useState } from 'react';
import { ModalOverlay } from './ModalOverlay';
import { STATUS_THEME_COLORS, STATUS_TINT_COLORS } from '../utils/statusColors';
import { useJobPolling } from '../hooks/useJobPolling';
import {
  DECAY_MAX,
  DECAY_MIN,
  DECAY_SLIDER_STEP,
  DEFAULT_DECAY,
  DEFAULT_RELEVANCE_MULTIPLIER,
  formatDecay,
  formatRelevanceMultiplier,
  RELEVANCE_MULT_MAX,
  RELEVANCE_MULT_MIN,
  RELEVANCE_SLIDER_STEP,
} from '../scoringConfig';
import {
  getTldrPromptStatus,
} from '../repositories/tldrPromptRepository';

/** Feed freshness relative to the saved prompt: rebuilding, in sync, or unknown. */
type RebuildPhase = 'idle' | 'rebuilding' | 'live';
const STATUS_POLL_MS = 3000;

interface TldrPromptSettings {
  description: string;
  freshness_constant: number;
  relevance_constant: number;
}

interface TldrPromptModalProps {
  description: string;
  decay: number;
  relevance: number;
  onSubmit: (settings: TldrPromptSettings) => Promise<void>;
  onClose: () => void;
}

export function TldrPromptModal({
  description,
  decay,
  relevance,
  onSubmit,
  onClose,
}: TldrPromptModalProps) {
  const [descriptionDraft, setDescriptionDraft] = useState(description);
  const [decayDraft, setDecayDraft] = useState(decay);
  const [relevanceDraft, setRelevanceDraft] = useState(relevance);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState<RebuildPhase>('idle');
  const [builtAt, setBuiltAt] = useState<string | null>(null);

  const dirty =
    descriptionDraft.trim() !== description.trim()
    || decayDraft !== decay
    || relevanceDraft !== relevance;

  // While a rebuild is pending, poll the feed's freshness relative to the saved
  // prompt so the admin can see the old feed flip to the new one. The rebuild
  // is a full LLM judging pass over fresh candidates, so this can take a while;
  // we keep serving the previous feed until it lands (no junk fallback).
  const { startPolling, stopPolling } = useJobPolling({
    pollJob: () => getTldrPromptStatus(),
    onUpdate: () => {},
    isTerminal: (status) => status.reflects_latest_prompt,
    onTerminal: (status) => {
      setBuiltAt(status.snapshot_refreshed_at);
      setPhase('live');
    },
    intervalMs: STATUS_POLL_MS,
  });
  useEffect(() => {
    if (phase !== 'rebuilding') {
      stopPolling();
      return;
    }
    startPolling();
    return stopPolling;
  }, [phase, startPolling, stopPolling]);

  const save = async () => {
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({
        description: descriptionDraft.trim(),
        freshness_constant: decayDraft,
        relevance_constant: relevanceDraft,
      });
      // Keep the modal open and switch to rebuild-watching so the admin knows
      // whether they're looking at the old feed or the freshly-judged one.
      setBuiltAt(null);
      setPhase('rebuilding');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save settings');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose} className="tldr-prompt-modal">
      <h2 className="tldr-prompt-modal-title">Edit TLDR feed prompt</h2>
      <div className="channel-heading-settings">
        <div className="channel-setting">
          <label className="channel-setting-label">Description</label>
          <textarea
            value={descriptionDraft}
            onChange={(e) => setDescriptionDraft(e.target.value)}
            rows={6}
            disabled={submitting}
            autoFocus
            style={{
              width: '100%',
              boxSizing: 'border-box',
              resize: 'vertical',
              border: '1px solid var(--line)',
              borderRadius: 8,
              padding: '12px',
              font: 'inherit',
              color: 'var(--ink)',
              background: 'var(--panel)',
            }}
          />
        </div>
        <div className="channel-setting">
          <label className="channel-setting-label">Recency decay</label>
          <div className="channel-setting-slider-row">
            <span className="channel-setting-endpoint">older</span>
            <input
              type="range"
              min={DECAY_MIN}
              max={DECAY_MAX}
              step={DECAY_SLIDER_STEP}
              value={decayDraft}
              onChange={(e) => setDecayDraft(parseFloat(e.target.value))}
              disabled={submitting}
              style={{ flex: 1 }}
            />
            <span className="channel-setting-endpoint">recent</span>
            <code className="channel-setting-value">{formatDecay(decayDraft)}</code>
            <button
              type="button"
              className="ghost-button channel-setting-reset"
              onClick={() => setDecayDraft(DEFAULT_DECAY)}
              disabled={submitting}
            >
              Reset
            </button>
          </div>
        </div>
        <div className="channel-setting">
          <label className="channel-setting-label">Relevance weighting</label>
          <div className="channel-setting-slider-row">
            <span className="channel-setting-endpoint">quality</span>
            <input
              type="range"
              min={RELEVANCE_MULT_MIN}
              max={RELEVANCE_MULT_MAX}
              step={RELEVANCE_SLIDER_STEP}
              value={relevanceDraft}
              onChange={(e) => setRelevanceDraft(parseFloat(e.target.value))}
              disabled={submitting}
              style={{ flex: 1 }}
            />
            <span className="channel-setting-endpoint">match</span>
            <code className="channel-setting-value">{formatRelevanceMultiplier(relevanceDraft)}</code>
            <button
              type="button"
              className="ghost-button channel-setting-reset"
              onClick={() => setRelevanceDraft(DEFAULT_RELEVANCE_MULTIPLIER)}
              disabled={submitting}
            >
              Reset
            </button>
          </div>
        </div>
        {error && <div className="prompt-toast prompt-toast-error">{error}</div>}
        {phase === 'rebuilding' && (
          <div className="prompt-toast" style={{ background: 'var(--wash, #f3f4f6)', color: 'var(--ink)' }}>
            ⟳ Rebuilding the feed against your new prompt — you're still seeing the
            previous feed. This re-judges fresh candidates and can take a bit.
          </div>
        )}
        {phase === 'live' && (
          <div className="prompt-toast prompt-toast-success" style={{ background: STATUS_TINT_COLORS.success, color: STATUS_THEME_COLORS.success }}>
            ✓ Feed now reflects your latest prompt
            {builtAt ? ` (built ${new Date(builtAt).toLocaleTimeString()})` : ''}.
          </div>
        )}
        <div className="channel-setting-actions">
          <button
            type="button"
            className="primary-button channel-setting-confirm"
            onClick={save}
            disabled={submitting || !dirty}
          >
            {submitting ? 'Saving...' : 'Save'}
          </button>
          <button
            type="button"
            className="ghost-button channel-setting-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            {phase === 'idle' ? 'Cancel' : 'Close'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
